import { useState, useRef, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Video } from "@/machines/types/video";
import { toast } from "react-hot-toast";

interface UseVideoPlaybackProps {
  currentVideo: Video | undefined;
  onVideoEnded?: () => void;
}

/**
 * WebKit's fullscreen entry points, which `lib.dom` does not declare.
 * `webkitRequestFullscreen` is Safari desktop's prefixed Element API;
 * `webkitEnterFullscreen` is iOS Safari's video-only native player (the ONLY
 * way to go fullscreen there — the Element API doesn't exist).
 */
type WebkitFullscreenVideo = HTMLVideoElement & {
  webkitRequestFullscreen?: () => void;
  webkitEnterFullscreen?: () => void;
};

export const useVideoPlayback = ({ currentVideo, onVideoEnded }: UseVideoPlaybackProps) => {
  const { t } = useTranslation("machines");
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);

  const handlePlaybackRateChange = (rate: number) => {
    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
  };

  const handlePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleSkipBack = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
  };

  const handleSkipForward = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.min(
      videoRef.current.duration,
      videoRef.current.currentTime + 5
    );
  };

  const handleDownload = () => {
    if (!currentVideo?.signedUrl) {
      toast.error(t("video.toast.noDownload"));
      return;
    }

    // Download is intentionally disabled for now. A bare `<a download>` is ignored
    // for cross-origin URLs, and the signed video URLs are cross-origin, so it
    // would navigate/open the clip instead of downloading it — re-enabling needs a
    // blob fetch (fetch -> objectURL -> download). Until then, tell the user
    // instead of silently doing nothing.
    toast(t("video.toast.downloadUnavailable"));
  };

  const handleShare = () => {
    if (!currentVideo?.signedUrl) {
      toast.error(t("video.toast.noShare"));
      return;
    }

    if (navigator.share) {
      navigator
        .share({
          title: t("video.share.title"),
          text: t("video.share.text"),
          url: currentVideo.signedUrl,
        })
        .then(() => toast.success(t("video.toast.shared")))
        .catch((error) => {
          if (error.name !== "AbortError") {
            toast.error(t("video.toast.shareFailed"));
          }
        });
    } else {
      navigator.clipboard.writeText(currentVideo.signedUrl);
      toast.success(t("video.toast.urlCopied"));
    }
  };

  const handleFullscreen = () => {
    const el = videoRef.current as WebkitFullscreenVideo | null;
    if (!el) return;

    // The standard API first, then the WebKit fallbacks. Guarding on
    // `requestFullscreen` ALONE made this button silently do nothing in Safari,
    // where the element carries only the prefixed entry points.
    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (el.webkitEnterFullscreen) {
      el.webkitEnterFullscreen();
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current || isSeeking) return;
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration);
    // Preserve playback rate across video changes
    videoRef.current.playbackRate = playbackRate;
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (onVideoEnded) {
      onVideoEnded();
    }
  };

  const handleSeek = (value: number[]) => {
    const newTime = value[0];
    setCurrentTime(newTime);
    setIsSeeking(true);
  };

  const handleSeekComplete = (value: number[]) => {
    if (!videoRef.current) return;
    const newTime = value[0];
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setIsSeeking(false);
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return {
    videoRef,
    isPlaying,
    setIsPlaying,
    currentTime,
    duration,
    handlePlayPause,
    handleSkipBack,
    handleSkipForward,
    handleDownload,
    handleShare,
    handleFullscreen,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleEnded,
    handleSeek,
    handleSeekComplete,
    formatTime,
    playbackRate,
    handlePlaybackRateChange,
  };
};
